import { all, call, cancelled, put, race, select } from 'redux-saga/effects';

import { selectTargetPlatform } from '@pressreader/appcore';
import { GeneratorResult, OperationStatus, takeIf } from '@pressreader/appstore';
import { loadMissingIssueInfo, loadMissingPublicationInfo } from '@pressreader/commerce';
import {
    AnyPurchaseProductDto,
    AvailableProductsDto,
    getAllAvailableProducts,
    getAvailableProducts,
    getCurrentSubscription,
    mapFromIssueInfoDto,
} from '@pressreader/commerce-api';
import {
    getIssueInfoByCid,
    getIssuePagesMetaData,
    getIssueToc,
    mapIssueInfoDto,
    mapIssueInfoDtoRestrictions,
    mapIssuePageMetadata,
} from '@pressreader/content-api';
import { IIssueInfo } from '@pressreader/content-types';
import { buildCidDateKey, extractCid, extractCidDateKeyString, extractDate } from '@pressreader/content-utils';
import { error } from '@pressreader/logger';
import { formatDate } from '@pressreader/resources';
import { ExtractReturnType, TargetPlatform } from '@pressreader/types';
import { toCamelCase } from '@pressreader/utils';

import { buildCidDateLanguageKey } from '../../../utils';
import { issueRestrictionsUpdated, loadIssueRestrictionsDone } from '../../actions';
import { loadIssueInfoBegan, loadIssueInfoDone, loadIssueInfoFailed } from '../../actions/issueinfo';
import { selectIsIssueInfoLoadingUnderSiblingLanguageKey, selectIssueInfoByCidDateLanguage } from '../../selectors/issueinfo';

import { isGenerationCurrent } from './generation';

function* fetchAvailableProducts(issueKey: string | undefined): GeneratorResult<AvailableProductsDto | []> {
    if (!issueKey) {
        return [];
    }
    try {
        return yield call(getAllAvailableProducts, issueKey);
    } catch (err) {
        error('Error fetching all available products:', err);
        return [];
    }
}

/**
 * Waits for the next completion of any issue-info load of `cid`, then re-enters the lookup: the
 * load in flight may commit under the key this request reads, and only its completion tells.
 */
function* waitForIssueInfoLoadOfCidAndRetry(cid: string, issueDate?: Date, languageCode?: string): GeneratorResult<IIssueInfo | null> {
    yield race({
        done: takeIf(loadIssueInfoDone, a => a.payload.issueInfo.newspaper.cid === cid),
        failed: takeIf(loadIssueInfoFailed, a => a.payload.cid === cid),
    });

    return yield* getOrWaitForIssueInfo(cid, issueDate, languageCode);
}

function* getOrWaitForIssueInfo(cid: string, issueDate?: Date, languageCode?: string): GeneratorResult<IIssueInfo | null> {
    const cidDateLanguageKey = buildCidDateLanguageKey({ cid, issueDate, languageCode });
    let data: ReturnType<typeof selectIssueInfoByCidDateLanguage> = yield select(selectIssueInfoByCidDateLanguage, {
        cid,
        issueDate,
        languageCode,
    });

    if (!data) {
        const dataWithoutLanguage: ReturnType<typeof selectIssueInfoByCidDateLanguage> = yield select(selectIssueInfoByCidDateLanguage, {
            cid,
            issueDate,
            languageCode: undefined,
        });

        if (dataWithoutLanguage?.info?.newspaper.isoLanguage === languageCode) {
            data = dataWithoutLanguage;
        }
    }

    const latest = !issueDate;

    // A dated lookup can race an in-flight LATEST load of the same cid (which commits under
    // the dated key only on completion). Wait for that load instead of double-fetching the
    // whole chain, then re-check. This wait and the latest re-check below both compensate
    // for the Done reducer deleting the latest-key entry (reducers/issueinfo.ts); a
    // latest-key alias kept there would reduce both to a plain select — tracked as #175966.
    if (!data && issueDate) {
        const latestEntry: ReturnType<typeof selectIssueInfoByCidDateLanguage> = yield select(selectIssueInfoByCidDateLanguage, {
            cid,
            issueDate: undefined,
            languageCode,
        });
        if (latestEntry?.loading === OperationStatus.Pending || latestEntry?.loading === OperationStatus.Queued) {
            return yield* waitForIssueInfoLoadOfCidAndRetry(cid, issueDate, languageCode);
        }
    }

    // The alias above needs the other key's entry LOADED - while it is still in flight its `info` is
    // null and nothing can tell yet whether its language is the newspaper's own. Wait for it the
    // same way, then re-enter: the Done reducer caches an original-language response under both key
    // shapes, so an equal-language load answers this request too. A response in another language
    // leaves the lookup a miss and the caller fetches its own; a Failed sibling is no wait at all.
    if (!data) {
        const siblingLoading: ReturnType<typeof selectIsIssueInfoLoadingUnderSiblingLanguageKey> = yield select(
            selectIsIssueInfoLoadingUnderSiblingLanguageKey,
            { cid, issueDate, languageCode },
        );
        if (siblingLoading) {
            return yield* waitForIssueInfoLoadOfCidAndRetry(cid, issueDate, languageCode);
        }
    }

    if (data && data.loading !== OperationStatus.None) {
        if (data.loading === OperationStatus.Done) {
            return (yield* isGenerationCurrent(data)) ? data.info : null;
        }

        if (data.loading === OperationStatus.Failed) {
            return null;
        }

        const { done }: { done?: ReturnType<typeof loadIssueInfoDone> } = yield race({
            done: takeIf(loadIssueInfoDone, a => {
                const { issueInfo } = a.payload;

                if (latest && a.payload.latest) {
                    return cid === issueInfo.newspaper.cid;
                }

                return buildCidDateLanguageKey(issueInfo.issueCidDateKey, languageCode) === cidDateLanguageKey;
            }),
            failed: takeIf(
                loadIssueInfoFailed,
                a => buildCidDateLanguageKey({ cid: a.payload.cid, issueDate: a.payload.issueDate, languageCode }) === cidDateLanguageKey,
            ),
        });

        if (done && latest) {
            // The Done reducer re-keys latest entries under the resolved date — re-check by
            // that key, or the concurrent latest waiter re-fetches the whole chain.
            return yield* getOrWaitForIssueInfo(cid, extractDate(done.payload.issueInfo.issueKey), languageCode);
        }

        return yield* getOrWaitForIssueInfo(cid, issueDate, languageCode);
    }

    return null;
}

async function getRelevantProductsForTheIssue(issueKey: string): Promise<AnyPurchaseProductDto[]> {
    const [availableProducts, currentSubscription] = await Promise.all([getAvailableProducts({ issueKey }), getCurrentSubscription()]);
    if (!currentSubscription?.productId) {
        return availableProducts;
    }
    return availableProducts?.filter(el => el.type === 'SingleIssue' || (currentSubscription && el.productId !== currentSubscription.productId));
}

function* loadIssueInfo(cid: string, issueDate?: Date, languageCode?: string, force?: boolean, loadAllAvailableProducts?: boolean) {
    let issueInfo = yield* getOrWaitForIssueInfo(cid, issueDate, languageCode);
    if (issueInfo && !force) {
        return issueInfo;
    }

    const platform: ReturnType<typeof selectTargetPlatform> = yield select(selectTargetPlatform);

    const latest = !issueDate;

    yield put(loadIssueInfoBegan({ cid, issueDate, languageCode }));

    try {
        const compactDate = issueDate ? formatDate(issueDate, 'yyyyMMdd') : undefined;

        const issueInfoDto: ExtractReturnType<typeof getIssueInfoByCid> = yield call(getIssueInfoByCid, cid, compactDate);

        const loadPurchaseInfo = !issueInfoDto.SubscriptionsInfo?.IsFreeContent && platform !== TargetPlatform.Voyager;
        // has been changed to use IsFreeContent instead of CouldPotentiallyBeRead cause of backend has been changed :
        // the new backend returns CouldPotentiallyBeRead === true when the item is available for reading by the current user
        // OR when an item can be obtained using the current subscription balance (without any explicit purchase)
        //
        //  at the same time, the SubscriptionsInfo?.IsFreeContent property returns true when the item could be read by the current user
        //  without any additional confirmation or purchase
        const {
            issueTocDto,
            productsDtos,
            availableProductsDtos,
        }: {
            issueTocDto: ExtractReturnType<typeof getIssueToc>;
            productsDtos: ExtractReturnType<typeof getRelevantProductsForTheIssue>;
            availableProductsDtos: ExtractReturnType<typeof getAllAvailableProducts>;
        } = yield all({
            issueTocDto: call(getIssueToc, {
                issue: issueInfoDto.Issue.Issue,
                version: issueInfoDto.Layout.LayoutVersion,
                expungeVersion: issueInfoDto.Issue.ExpungeVersion,
                lng: languageCode,
            }),
            productsDtos: call(() => (loadPurchaseInfo ? getRelevantProductsForTheIssue(issueInfoDto.Issue.Issue) : [])),
            availableProductsDtos: call(fetchAvailableProducts, loadPurchaseInfo && loadAllAvailableProducts ? issueInfoDto.Issue.Issue : undefined),
        });

        issueInfo = mapIssueInfoDto(issueInfoDto, issueTocDto, productsDtos, availableProductsDtos);

        const issueRestrictions = mapIssueInfoDtoRestrictions(issueInfoDto);
        const pubRestrictionsCid = extractCidDateKeyString(issueInfo.issueKey);
        yield put(issueRestrictionsUpdated({ cid: pubRestrictionsCid, restrictions: issueRestrictions }));

        const { singleIssueProduct } = issueInfo.purchaseInfo;
        if (singleIssueProduct && !singleIssueProduct.issueInfo && singleIssueProduct.issueKey === issueInfoDto.Issue.Issue) {
            // The buy-this-issue product refers to the very issue this dto describes — map its
            // slice from the response in hand so loadMissingIssueInfo below doesn't re-download
            // the same document by key. The fetch remains the fallback for a product that
            // references a different issue.
            singleIssueProduct.issueInfo = mapFromIssueInfoDto(issueInfoDto);
        }

        const { issuePagesMeta }: { issuePagesMeta: ExtractReturnType<typeof getIssuePagesMetaData> } = yield all({
            issuePagesMeta: getIssuePagesMetaData(
                issueInfo.issueKey,
                issueInfo.pages.map(p => p.pageNumber),
            ),
            missingIssueInfo: issueInfo.purchaseInfo.singleIssueProduct && loadMissingIssueInfo([issueInfo.purchaseInfo.singleIssueProduct]),
            missingPublicationInfo:
                issueInfo.purchaseInfo.singleTitleProduct && loadMissingPublicationInfo([issueInfo.purchaseInfo.singleTitleProduct]),
        });
        const issuePagesMetadata = issuePagesMeta.map(mapIssuePageMetadata);

        issueInfo.pages = issueInfo.pages.map(p => {
            const pageArticles = p.articles;
            const issuePageMeta = issuePagesMetadata.find(pm => pm.pageNumber === p.pageNumber);
            const issuePageMetaArticles = issuePageMeta?.articles || [];
            const articles = pageArticles.map(article => {
                const issuePageMetaArticle = issuePageMetaArticles.find(a => a.articleId === article.id);
                if (!issuePageMetaArticle) {
                    return article;
                }
                return { ...issuePageMetaArticle, ...article, primaryArticleId: issuePageMetaArticle.primaryArticleId };
            });
            return { ...p, articles };
        });

        yield put(loadIssueInfoDone({ latest, issueInfo, languageCode }));
        yield put(
            loadIssueRestrictionsDone({ cidDateKey: buildCidDateKey(cid, issueInfo.date), restrictions: toCamelCase(issueInfoDto.Restrictions) }),
        );

        return issueInfo;
    } catch (e) {
        error(e);
        yield put(loadIssueInfoFailed({ cid, issueDate, languageCode }));
    } finally {
        // Cancellation unwinds a saga through `finally`, never `catch`, and callers of this
        // loader run under takeLatest. Failing the entry on cancel keeps the Pending marked by
        // `loadIssueInfoBegan` from outliving its owner: a stranded Pending entry blocks every
        // future waiter in `getOrWaitForIssueInfo`, while a Failed one releases the waiters and
        // is re-fetched by the next `loadIssueInfo` call.
        const isCancelled: boolean = yield cancelled();
        if (isCancelled) {
            yield put(loadIssueInfoFailed({ cid, issueDate, languageCode }));
        }
    }

    return null;
}

/**
 * Either fetches or retrieves from store the issue info by issue key.
 */
function* loadIssueInfoByIssueKey(issueKey: string | undefined, languageCode?: string, force?: boolean) {
    if (!issueKey) {
        return null;
    }

    const cid = extractCid(issueKey);
    const issueDate = extractDate(issueKey);

    return yield* loadIssueInfo(cid, issueDate, languageCode, force);
}

export { loadIssueInfo, loadIssueInfoByIssueKey };
